import React, { useState } from 'react';
import { FolioExpansionData, AgentCostDetail } from '../types';

interface FolioStepCardProps {
  data: FolioExpansionData;
}

const formatCost = (costDetail: AgentCostDetail): string => {
  const total = (costDetail.inputTokens / 1_000_000) * costDetail.costPerMillionInputTokens +
                (costDetail.outputTokens / 1_000_000) * costDetail.costPerMillionOutputTokens;
  return total < 0.01 ? `$${total.toFixed(4)}` : `$${total.toFixed(2)}`;
};

export const FolioStepCard: React.FC<FolioStepCardProps> = ({ data }) => {
  const [expanded, setExpanded] = useState(false);

  if (data.wasSkipped) {
    return (
      <div data-component="FolioStepCard" className="mb-4 p-3 bg-gray-50 rounded border border-gray-200">
        <div className="flex items-center gap-2">
          <span className="text-sm">📚</span>
          <span className="text-sm font-medium text-gray-600">
            FOLIO Ontology — Skipped (complexity {data.complexityScore.toFixed(1)} below threshold {data.threshold})
          </span>
        </div>
      </div>
    );
  }

  return (
    <div data-component="FolioStepCard" className="mb-4 p-3 bg-purple-50 rounded border border-purple-200">
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm">📚</span>
          <span className="text-sm font-medium text-purple-800">
            FOLIO Ontology Expansion
          </span>
          <span className="text-xs text-purple-600 bg-purple-100 px-1.5 py-0.5 rounded">
            {data.termCount} terms → {data.conceptCount} concepts
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          {data.costDetail && <span className="text-green-700">{formatCost(data.costDetail)}</span>}
          <span>{data.elapsedMs}ms</span>
          <span className="text-gray-400">{expanded ? '▼' : '▶'}</span>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 space-y-3">
          {/* Terms */}
          {data.terms.length > 0 && (
            <div>
              <span className="text-[10px] font-medium text-gray-500 uppercase">Generated Terms ({data.termCount})</span>
              <div className="flex flex-wrap gap-1 mt-0.5">
                {data.terms.map((term, i) => (
                  <span key={i} className="inline-block bg-purple-100 text-purple-800 text-[11px] px-1.5 py-0.5 rounded">{term}</span>
                ))}
              </div>
            </div>
          )}

          {/* Concepts */}
          {data.concepts.length > 0 && (
            <div>
              <span className="text-[10px] font-medium text-gray-500 uppercase">Found Concepts ({data.conceptCount})</span>
              <div className="mt-1 max-h-64 overflow-auto text-[11px]">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="text-left text-[10px] text-gray-400 uppercase">
                      <th className="pb-0.5 pr-2">Concept</th>
                      <th className="pb-0.5 pr-2">Relationship</th>
                      <th className="pb-0.5">Definition</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.concepts.map((c, i) => (
                      <tr key={i} className="border-t border-gray-200 align-top">
                        <td className="py-1 pr-2 font-medium whitespace-nowrap">{c.Label || c.label}</td>
                        <td className="py-1 pr-2 text-gray-500 whitespace-nowrap">{c.Relationship || c.relationship}</td>
                        <td className="py-1 text-gray-600">{c.Definition || c.definition || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {data.conceptCount === 0 && (
            <p className="text-xs text-gray-500 italic">No matching concepts found in FOLIO ontology.</p>
          )}

          {/* Cost detail */}
          {data.costDetail && (
            <div className="text-xs text-gray-500 border-t border-purple-100 pt-2">
              <span className="font-medium">{data.costDetail.modelId}</span>
              {' · '}
              {(data.costDetail.inputTokens + data.costDetail.outputTokens).toLocaleString()} tokens
              {' · '}
              {formatCost(data.costDetail)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
